<template>
    <div class="replay-controls">
        <span class="replay-control-wrapper">
            <span class="replay-control" :class="{['control-disabled']: onFirstSnapshot }" @click="goToFirst">
                <fa-icon class="icon" icon="angle-double-left" />
            </span>
        </span>
        <span class="replay-control-wrapper">
            <span class="replay-control" :class="{['control-disabled']: onFirstSnapshot }" @click="prev">
                <fa-icon class="icon" icon="angle-left" />
            </span>
        </span>
        <span class="replay-control-wrapper play-control-wrapper">
            <span class="replay-control play-control" @click="togglePlay">
                <fa-icon class="icon" icon="play" v-if="!playing" />
                <fa-icon class="icon" icon="pause" v-if="playing" />
            </span>
        </span>
        <span class="replay-control-wrapper">
            <span class="replay-control" :class="{['control-disabled']: onLastSnapshot }" @click="next">
                <fa-icon class="icon" icon="angle-right" />
            </span>
        </span>
        <span class="replay-control-wrapper">
            <span class="replay-control" :class="{['control-disabled']: onLastSnapshot }" @click="goToLast">
                <fa-icon class="icon" icon="angle-double-right" />
            </span>
        </span>
        <div class="replay-control-slider">
            <input
                type="range"
                min="0"
                :max="snapshotCount - 1"
                :value="snapshotIndex"
                class="slider"
                @input="onSlideSnapshots"
            />
        </div>
    </div>
</template>

<script>
import throttle from 'lodash.throttle';

export default {
    name: 'ReplayControls',
    created() {
        document.removeEventListener('keydown', this.handlePlayback);
        document.addEventListener('keydown', this.handlePlayback);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handlePlayback);
        this.pause();
    },
    watch: {
        playing(current, previous) {
            if (!previous && current && this.hasSnapshotsLeft) {
                this.autoPlay();
            }
        },
        snapshotIndex(current, previous) {
            if (!previous !== current &&
                this.playing &&
                !this.hasSnapshotsLeft) {
                this.pause();
            }
        },
    },
    computed: {
        snapshotCount() {
            return this.replayState.snapshotCount;
        },
        snapshotIndex() {
            return this.replayState.snapshotIndex;
        },
        replayState() {
            return this.$store.state.replayState;
        },
        onFirstSnapshot() {
            return !this.snapshotIndex;
        },
        onLastSnapshot() {
            return this.snapshotIndex === this.snapshotCount - 1;
        },
        hasSnapshotsLeft() {
            return this.snapshotIndex < this.snapshotCount - 1;
        },
        playing() {
            return this.replayState.playing;
        },
    },
    methods: {
        goToFirst() {
            if (this.playing) {
                this.pause();
            }

            this.$store.dispatch('setSnapshotIndex', 0);
        },
        goToLast() {
            if (this.playing) {
                this.pause();
            }
            this.$store.dispatch('setSnapshotIndex', this.snapshotCount - 1);
        },
        next(event, pause = true) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.playing && pause) {
                this.pause();
            }

            const nextIndex = this.snapshotIndex + 1;

            if (nextIndex < this.snapshotCount) {
                this.$store.dispatch('setSnapshotIndex', nextIndex);
            }
        },
        prev(event) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.playing) {
                this.pause();
            }

            const prevIndex = this.snapshotIndex - 1;

            if (prevIndex >= 0) {
                this.$store.dispatch('setSnapshotIndex', prevIndex);
            }
        },
        pause() {
            this.$store.dispatch('setPlayingState', false);
        },
        togglePlay(event) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (!this.playing && !this.hasSnapshotsLeft) {
                this.$notify({
                    group: 'app',
                    clean: true,
                });

                this.$notify({
                    group: 'app',
                    type: 'info',
                    text: 'No more moves available.',
                });
                return;
            }

            this.$store.dispatch('setPlayingState', !this.playing);
        },
        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        autoPlay: throttle(async function autoPlay() {
            if (this.playing && this.hasSnapshotsLeft) {
                await this.next(null, false);

                await this.sleep(1500);

                if (this.playing) {
                    await this.autoPlay();
                }
            }
        }, 1500),
        onSlideSnapshots: throttle(async function onSlideSnapshots(event) {
            const nextIndex = parseInt(event.target.value, 10);
            this.$store.dispatch('setSnapshotIndex', nextIndex);

            if (nextIndex >= this.snapshotCount - 1) {
                this.pause();
            }
        }, 100),
        handlePlayback(event) {
            const arrowUp = 38;
            const arrowLeft = 37;
            const arrowRight = 39;
            const arrowDown = 40;
            const spacebar = 32;

            if (event.isComposing || event.keyCode === 229) {
                return;
            }

            if (event.keyCode === arrowUp) {
                this.goToFirst();
            }

            if (event.keyCode === arrowLeft) {
                this.prev();
            }

            if (event.keyCode === arrowRight) {
                this.next();
            }

            if (event.keyCode === arrowDown) {
                this.goToLast();
            }

            if (event.keyCode === spacebar) {
                event.preventDefault();
                this.togglePlay();
            }
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .replay-controls {
        position: relative;

        .replay-control-wrapper {
            background: white;
            border-radius: 50%;
            box-shadow: 0 0 2px 1px rgba(150, 150, 150, .6);
            cursor: pointer;
            display: inline-flex;
            justify-content: center;
            margin: 0 2.5px;
            min-height: 20px;
            min-width: 20px;
            padding: 2.5px;
            position: relative;
            user-select: none;

            &.play-control-wrapper {
                min-height: 26px;
                min-width: 26px;

                .replay-control {
                    min-height: 24px;
                    min-width: 24px;

                    .icon {
                        font-size: .8em;
                    }
                }
            }

            .replay-control {
                align-self: center;
                border-radius: 50%;
                box-shadow: 0 0 2px 1px rgba(150, 150, 150, .6);
                min-height: 20px;
                min-width: 20px;

                .icon {
                    font-size: .9em;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);


                }

                &.play-control {
                    color: $berserker;
                }
            }
        }

        .replay-control-slider {
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
            margin: 0 auto;
            width: 100%;
        }

        .slider {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 6px;
            background: $off-white;
            outline: none;
            -webkit-transition: .2s;
            transition: opacity .2s;
            border-radius: 8px;
            box-shadow: 1px 1px 2px 1px $medium-gray;
        }

        $sliderSize: 18px;

        .slider::-webkit-slider-thumb {
            appearance: none;
            width: 14.5px;
            height: 14.5px;
            background: $berserker;
            cursor: pointer;
            border-radius: 50%;
        }

        .slider::-moz-range-thumb {
            width: 14.5px;
            height: 14.5px;
            background: $berserker;
            cursor: pointer;
            border-radius: 50%;
        }
    }

    .mobile-replay-controls {

        .replay-controls {
            width: 85%;
            display: flex;
            justify-content: space-evenly;

            .replay-control-wrapper {
                align-self: center;
            }

            .replay-control-slider {
                display: none;
            }
        }
    }

    .board-wrapper {

       .replay-controls {
            position: absolute;
            top: 30px;
            left: 0;
       }
    }

    @media only screen and (min-width: $screen-sm) {
        .replay-controls {

            .replay-control-wrapper {
                padding: 3px;
                min-height: 17px;
                min-width: 17px;

                &.play-control-wrapper {
                    min-height: 23px;
                    min-width: 23px;

                    .replay-control {
                        min-height: 22px;
                        min-width: 22px;
                    }
                }

                .replay-control {
                    min-height: 17px;
                    min-width: 17px;
                }
            }
        }
    }

</style>
